import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useMetaTracking } from '../hooks/useMetaTracking';
import QuizSection from '../components/home/QuizSection';

const { FiScissors, FiStar, FiChevronRight, FiMessageCircle, FiCheckCircle, FiShield, FiSearch } = FiIcons;

const Home = () => {
  const { trackContact } = useMetaTracking();

  return (
    <div className="bg-white">
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-white pt-16 pb-24 lg:pt-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8 }}>
              <div className="inline-flex items-center space-x-2 bg-blue-50 px-4 py-2 rounded-full text-blue-900 font-bold text-sm mb-6 border border-blue-100">
                <SafeIcon icon={FiStar} className="w-4 h-4" />
                <span>Rua Delfim Moreira, 125 - Centro</span>
              </div>
              <h1 className="text-5xl lg:text-7xl font-extrabold text-gray-900 leading-tight mb-8">
                Estética Pet com <span className="text-blue-900">Olhar Veterinário.</span>
              </h1>
              <p className="text-xl text-gray-600 mb-10 leading-relaxed max-w-lg">
                Referência em Banho e Tosa em Juiz de Fora. Cuidado premium e protocolos de saúde sob supervisão técnica.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <a href="http://wa.me/5532999857587" onClick={trackContact} className="bg-blue-900 text-white px-8 py-5 rounded-2xl font-bold text-lg shadow-xl shadow-blue-900/20 hover:bg-blue-950 transition-all flex items-center justify-center gap-2">
                  Chamar no WhatsApp <SafeIcon icon={FiMessageCircle} />
                </a>
                <Link to="/servicos" className="border-2 border-gray-100 text-gray-700 px-8 py-5 rounded-2xl font-bold text-lg hover:bg-gray-50 transition-all flex items-center justify-center gap-2">
                  Ver Serviços <SafeIcon icon={FiScissors} className="text-blue-900" />
                </Link>
              </div>
            </motion.div>
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1 }} className="relative">
              <div className="aspect-square rounded-[600px] overflow-hidden shadow-2xl border-8 border-white">
                <img src="https://images.unsplash.com/photo-1516734212186-a967f81ad0d7?auto=format&fit=crop&q=80&w=1200" alt="Banho e Tosa Juiz de Fora UniPET" className="w-full h-full object-cover" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Autoridade Local */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl">
            <h2 className="text-3xl md:text-5xl font-extrabold text-gray-900 mb-8 leading-tight">
              A maior autoridade em <span className="text-blue-900">Estética Animal no Centro de JF</span>
            </h2>
            <div className="prose prose-lg text-gray-600 max-w-none font-medium">
              <p className="mb-6">
                Encontrar o <b>melhor banho e tosa em Juiz de Fora</b> exige confiança. A UniPET oferece uma estrutura moderna com <b>supervisão técnica veterinária presencial</b>, algo único na região central.
              </p>
              <p>
                Localizada na <b>Rua Delfim Moreira</b>, somos especialistas em <b>tosa na tesoura, banhos terapêuticos e estética felina</b>. Utilizamos apenas insumos de alto padrão para entregar o resultado que seu pet merece.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Quiz Section Integration */}
      <QuizSection />

      {/* Promoção 15% OFF */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-r from-blue-900 to-blue-950 rounded-[40px] p-8 md:p-16 relative overflow-hidden shadow-2xl shadow-blue-900/10">
            <div className="relative z-10 grid md:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl md:text-5xl font-extrabold text-white mb-6 leading-tight">
                  Ganhe 15% OFF no primeiro banho.
                </h2>
                <p className="text-blue-100 mb-8 font-medium">Para aplicar seu cupom, basta chamar nossa equipe no WhatsApp para solicitar o agendamento e informar o código.</p>
                <Link to="/cupons" className="inline-flex items-center px-8 py-4 bg-orange-500 text-white rounded-2xl font-bold text-lg hover:bg-orange-600 transition-all shadow-xl shadow-black/20">
                  Resgatar Cupom UniPET <SafeIcon icon={FiChevronRight} className="ml-2" />
                </Link>
              </div>
              <div className="hidden md:block text-center">
                <div className="inline-block bg-white/5 backdrop-blur-md rounded-full px-12 py-12 border border-white/10">
                  <span className="block text-7xl font-black text-white">15%</span>
                  <span className="block text-blue-200 font-bold tracking-[0.2em] uppercase text-sm">DESCONTO JF</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Diferenciais */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-blue-50 p-8 rounded-3xl border border-blue-100">
                <SafeIcon icon={FiShield} className="text-blue-900 w-10 h-10 mb-4" />
                <h4 className="font-bold text-gray-900 mb-2">Higiene</h4>
                <p className="text-sm text-gray-500 font-bold">Toalhas esterilizadas individualmente.</p>
              </div>
              <div className="bg-orange-50 p-8 rounded-3xl border border-orange-100">
                <SafeIcon icon={FiScissors} className="text-orange-600 w-10 h-10 mb-4" />
                <h4 className="font-bold text-gray-900 mb-2">Técnica</h4>
                <p className="text-sm text-gray-500 font-bold">Especialistas em tosa na tesoura.</p>
              </div>
              <div className="col-span-2 bg-gray-50 p-8 rounded-3xl border border-gray-100">
                <p className="text-gray-600 font-bold italic">"A UniPET une o melhor do banho e tosa presencial com a segurança de uma supervisão veterinária constante."</p>
              </div>
            </div>
            <div>
              <h2 className="text-3xl md:text-5xl font-extrabold text-gray-900 mb-8 leading-tight">
                Cuidado que vai <span className="text-blue-900">além da estética.</span>
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed mb-6 font-medium">
                Nosso foco é a saúde e o bem-estar do seu pet. Cada banho é uma oportunidade de monitorar a saúde da pele e pelagem, com a garantia de quem entende de medicina veterinária.
              </p>
              <ul className="space-y-4">
                <li className="flex items-center gap-3 text-gray-800 font-extrabold">
                  <SafeIcon icon={FiCheckCircle} className="text-green-500" /> Ambiente climatizado e seguro
                </li>
                <li className="flex items-center gap-3 text-gray-800 font-extrabold">
                  <SafeIcon icon={FiCheckCircle} className="text-green-500" /> Produtos premium Hydra e Artero
                </li>
                <li className="flex items-center gap-3 text-gray-800 font-extrabold">
                  <SafeIcon icon={FiCheckCircle} className="text-green-500" /> Protocolos de baixo estresse
                </li>
              </ul>
              <div className="mt-10">
                <a href="https://wa.me/5532999857587" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-3 bg-blue-900 text-white px-8 py-4 rounded-2xl font-bold hover:bg-blue-950 transition-all">
                   Chamar no WhatsApp <SafeIcon icon={FiMessageCircle} />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;